export const spacePlanningEn = {
  pageTitle: "Space Planning",
  pageSubtitle:
    "Review, edit and approve pending planograms, view active planograms, or browse historical records",
  viewerPageSubtitle:
    "View delivered planograms waiting to go live, the one active in each store, or earlier ones",

  banner: {
    title: "Bring Your Planograms to Life",
    description:
      "Manage the full planogram lifecycle in one place. Review pending planograms generated from your previous assortment simulations, validate their performance metrics, and edit them as needed before approving for activation — or monitor active planograms across all your stores. Use filters and bulk actions to streamline your workflow and move faster from decision to deployment.",
  },

  tabs: {
    pending: "Pending Planograms",
    active: "Active Planograms",
    historical: "Historical Planograms",
  },

  filters: {
    viewOn: "View on:",
    allChains: "All Chains",
    allStores: "All Stores",
    allCategories: "All Categories",
    searchPlaceholder: "Search by store, ID, or SIM-id...",
    filterPogs: "Filter store POGs by:",
    latest: "Latest",
    oldest: "Oldest",
    activeFilters: "Active filters:",
    clearAll: "Clear all",
    commercialChain: "Commercial Chain",
    store: "Store",
    category: "Category",
    assortment: "Assortment",
    search: "Search",
    searchButton: "Search",
    scoreBelow: "Show plans scoring below",
    scoreBelowTag: "{{score}} below {{threshold}}",
  },

  pagination: {
    rowsPerPage: "Rows per page:",
  },

  results: {
    showing: "Showing {{start}}-{{end}} of {{total}} stores",
    selected: "({{count}} selected)",
  },

  columns: {
    storeCode: "Store Code",
    pogName: "POG Name",
    storeStatus: "Store Status",
    pogId: "POG ID",
    category: "Category",
    goLiveDate: "Go-live Date",
    assortment: "Assortment",
    returnOnSpace: "Return on Space",
    coreRange: "Core Range",
    shelfAlignment: "Shelf Alignment",
    dogms: "DOGMS",
    aesthetics: "Aesthetics",
    size: "Size",
    warnings: "Warnings",
    lastEdited: "Last Edited",
    archivedAt: "Archived",
    actions: "Actions",
    view: "View",
    analysis: "Analysis",
    logs: "Logs",
  },

  status: {
    pending: "Pending",
    active: "Active",
    historical: "Historical",
    notAvailable: "N/A",
    notScored: "Not scored",
    goLiveNotSet: "Not set",
    delivered: "Delivered",
    deliveredDetail: "Delivered to the store; it goes live on its go-live date",
    loading: "Loading planograms...",
    loadError: "Unable to load planograms.",
    loadErrorToastTitle: "Failed to load space planning data",
    loadErrorToastMessage: "Could not load these planograms: {{sources}}.",
    processing: "Processing...",
    error: "Processing error",
    updateFailed: "Update failed",
    updateFailedDetail:
      "The update API ran but none of the approved SKU inserts, removals, or swaps were applied. The planogram reflects the parent layout, not the approved changes.",
    updatePartial: "Partially applied",
    updatePartialDetail:
      "The update API ran and some of the approved SKU inserts, removals, or swaps were applied. Others were skipped (no compatible shelf, PNL impact, or other Frogger constraints).",
  },

  actions: {
    update: "Update",
    approve: "Approve",
    edit: "Edit",
    viewEdit: "View / Edit",
    updateSelected: "Update Selected ({{count}})",
    approveSelected: "Approve Selected ({{count}})",
    deleteSelected: "Delete Selected ({{count}})",
    reviewAll: "Review all ({{count}})",
    violations: "Violations ({{count}})",
    reports: "Reports ({{count}})",
    storeIntegrityReport: "Store Integrity Report",
    categoryRangeReviewReport: "Category Range Review Report",
    export: "Export ({{count}})",
  },

  toasts: {
    approvalsSuccess: "Planograms approved",
    approvalsError: "Failed to approve planograms",
    missingPogReference: "Missing merchflow/POG reference for this row",
    pogDataUnavailable: "POG data unavailable",
  },

  noResults: "No planograms found",

  logs: {
    title: "Activity Logs",
    exportLogs: "Export Logs",
    entries: {
      activated: "Planogram Activated",
      analysisRun: "Performance Analysis Run",
      compliancePassed: "Compliance Check Passed",
      warningDetected: "Warning Detected",
      auditCompleted: "Store Audit Completed",
    },
  },

  preview: {
    title: "Planogram Preview",
    category: "Category",
    assortment: "Assortment",
    activeDate: "Active Date",
    size: "Size",
    segments: "{{count}} segments",
    performanceMetrics: "Performance Metrics",
    status: "Status",
    returnOnSpace: "Return on Space",
    coreRange: "Core Range",
    shelfAlignment: "Shelf Alignment",
    dogms: "DOGMS",
    aesthetics: "Aesthetics",
    planogramLayout: "Planogram Layout",
    shelf: "Shelf {{number}}",
    section: "{{brand}} Section",
    totalSegments: "Total Segments",
    totalSkus: "Total SKUs",
    close: "Close",
    export: "Export",
    editPlanogram: "Edit Planogram",
  },

  history: {
    title: "Planogram History",
    approvedBy: "Approved by:",
    activated: "Activated:",
    deactivated: "Deactivated:",
    close: "Close",
    exportHistory: "Export History",
  },

  confirmUpdate: {
    singleTitle: "Confirm Planogram Update",
    singleSubtitle: "Review the changes before proceeding",
    bulkTitle: "Confirm Bulk Update",
    bulkSubtitle: "You are about to update {{count}} planograms",
    bulkSummary: "Bulk Update Summary",
    bulkSummaryDesc: "{{count}} planograms will be activated",
    whatWillHappen: "What will happen:",
    step1Title: "Activate new planogram",
    step1Desc: "Version {{id}} will be set as Active",
    step2Title: "Update store planogram",
    step2Desc: "The planogram will be marked as approved and live in Planograms",
    step3Title: "Archive previous version",
    step3Desc: "The current active planogram will be archived (if any)",
    step4Title: "Remove from pending",
    step4Desc: "This planogram will be removed from the Space Planning queue",
    planogramDetails: "Planogram Details",
    categoryLabel: "Category:",
    sizeLabel: "Size:",
    sizeSegments: "{{count}} segments",
    activeDateLabel: "Active Date:",
    endDateLabel: "End Date:",
    selectedPlanograms: "Selected Planograms ({{count}}):",
    warningTitle: "This action cannot be undone",
    warningDesc:
      "Once activated, this planogram will immediately replace the current one and affect store operations.",
    warningDescBulk:
      "Once activated, these {{count}} planograms will immediately replace current ones and affect store operations.",
    cancel: "Cancel",
    confirm: "Confirm Update",
    confirmBulk: "Confirm Update ({{count}})",
    na: "N/A",
  },

  confirmDelete: {
    title: "Delete Selected Planograms",
    subtitle: "You are about to delete {{count}} pending planogram(s)",
    selectedPlanograms: "Selected Planograms ({{count}}):",
    warningTitle: "This action cannot be undone",
    warningDesc:
      "These {{count}} planogram(s) will be removed from the Space Planning queue. Planograms that are currently processing will be skipped.",
    cancel: "Cancel",
    confirm: "Delete Selected ({{count}})",
    toasts: {
      deletedWithSkipped: "Deleted {{deleted}} planogram(s), skipped {{skipped}} in progress",
      deleted: "Planograms deleted",
      failed: "Failed to delete planograms",
      couldNotDelete: "Could not delete {{count}} planogram(s) — missing planogram reference",
    },
  },

  review: {
    prepared: {
      pendingStorageUnavailable:
        "Browser session storage is unavailable. Enable it before applying so an interrupted submission can be recovered safely. No new submission was sent.",
      invalidPending:
        "The saved pending request is invalid. Refresh saved job status and review the current proposals before starting a new request.",

      loadingOutcomes: "Loading per-store outcomes…",
      outcomesFailed:
        "Per-store outcomes could not be refreshed. Saved job counts remain authoritative.",

      checkingIssues: "Checking plan issues…",
      queueRefreshFailed:
        "The current plans could not be refreshed. Retry Refresh status before reviewing a new proposal.",
      scanWholeFlow:
        "Scan coverage describes the whole merchflow. Review and apply are limited to the selected chain.",
      scanUpdated: "Last scan update {{time}}",

      chooseFlow: "Choose a merchflow to review",
      chooseFlowDetail:
        "Choose one merchflow before preparing, reviewing or applying fixes. Category and chain filters remain in effect.",
      flowLoadFailed: "The available merchflows could not be loaded. Retry to choose a scope.",
      flowOption: "Merchflow {{id}} · {{count}} pending stores",

      submitting: "Submitting captured fixes…",
      queueing: "Queueing scan…",
      refreshStatus: "Refresh status",
      savedCanvas: "Current saved plan",
      originalCanvas: "Original plan",
      preparedCanvas: "Prepared proposal",
      savedScore: "Current saved plan score",
      scan: "Scan: {{completed}} / {{eligible}} finished · {{unscanned}} unscanned · {{queued}} queued · {{scanning}} scanning · {{calculating}} calculating · {{failed}} failed · {{seconds}}s elapsed",
      proposalRefreshError: "Proposal refresh failed. Saved application outcomes remain available.",
      jobRefreshError: "Application status refresh failed. Committed results are unchanged.",
      showAll: "Show all stores",
      recover: "Recover pending request",
      pending: "Pending captured proposals: {{ids}}",
      tally: "{{nothingSafe}} nothing safe · {{preparing}} preparing · {{failed}} failed",
      scanFailed: "Preparation failed for this store",
      scanFailedUnknown:
        "The server could not prepare this plan. Retry to request a fresh preparation.",
      retryScan: "Retry preparation for this store",
      prepare: "Prepare again",
      job: "Apply job · {{status}} · {{done}}/{{total}}",
      cancel: "Cancel pending items",
      retry: "Retry failed items only",
      outcomes: "Per-store outcomes",
      plan: "Plan {{id}}",
      unavailable: "unavailable",
      stale: "Prepare and review a fresh proposal before applying.",
      actionError: "Job action failed. Refresh status before trying again.",
      noSafeNotes: "Why no safe fix was prepared",
      queuedToast: "Prepared fixes queued. Progress is saved on the server.",
      retrySubmission: "Retry submission to recover the same request.",
      undoneToast: "Fix undone: the plan is back as it was. Redo puts it back.",
      prepareFirst: "Prepare fixes",
      narrowed: "The player is showing only the plans you picked.",
      notice: {
        title: "Fixes ready",
        body: "{{ready}} ready to review · {{nothingSafe}} with no safe fix · {{failed}} failed",
        ask: "Notify me when it's done",
        on: "You'll get a browser notification when preparation finishes.",
        blocked:
          "Browser notifications are blocked for this site; the tab title will say when it's done.",
      },
      steps: {
        details: "Details: scan status, history and everything else",
      },
      elapsedRunning: "Running for {{duration}} · last updated {{time}}",
      history: "Apply history ({{count}})",
      elapsedReadable: "Took {{duration}} · last updated {{time}}",
      duration: {
        hours: "{{hours}} h {{minutes}} min",
        minutes: "{{minutes}} min {{seconds}} s",
        seconds: "{{seconds}} s",
      },
      status: {
        idle: "No fixes prepared yet",
        idleDetail:
          "Preparing checks every pending plan in this merchflow on the server. It usually takes several minutes, and you can leave and come back.",
        waiting_one: "Waiting for the server to start · 1 store queued",
        waiting_other: "Waiting for the server to start · {{count}} stores queued",
        waitingDetail: "Queued {{duration}} ago. Calculation starts when a worker is free.",
        working: "Preparing fixes · {{done}} of {{total}} stores checked",
        workingDetail: "{{inProgress}} calculating · {{queued}} waiting · running for {{duration}}",
        delayed: "Preparation may be stalled",
        delayedDetail:
          "No progress reported for {{duration}}. Results already prepared stay available; refresh status to check again.",
        done: "Every store checked ({{total}})",
        donePartial: "{{done}} of {{total}} stores checked",
        doneDetail: "Finished in {{duration}}.",
        doneWithFailures: "{{done}} of {{total}} stores checked · {{failed}} could not be prepared",
        doneWithFailuresDetail: "Open a store that failed to retry its preparation.",
        reviewFailed: "Open stores that failed ({{count}})",
        unscanned_one: "1 store not checked yet; prepare again to include it",
        unscanned_other: "{{count}} stores not checked yet; prepare again to include them",
        lastUpdate: "last update {{duration}} ago",
        meanwhile:
          "You can review ready stores now. Progress is saved on the server, so you can leave this page and come back.",
        results:
          "{{ready}} ready to review · {{nothingSafe}} with no safe fix · {{failed}} failed · {{notNeeded}} need no fix",
        meanwhileNothingYet:
          "Stores appear here to review as soon as each one is ready. Progress is saved on the server, so you can leave this page and come back.",
        technical: "Technical details",
      },
      decision: {
        cancel: "Cancel",
      },
      outcome: {
        applying: "Applying fixes · {{done}} of {{total}} stores done",
        applyingDetail: "Saved on the server, so you can leave this page.",
        applied_one: "Changes applied to {{count}} store",
        applied_other: "Changes applied to {{count}} stores",
        noneApplied: "No changes were applied",
        undone_one: "1 store was undone afterwards and is back as it was",
        undone_other: "{{count}} stores were undone afterwards and are back as they were",
        loading: "Loading per-store results…",
        stillBlocked_one: "1 applied plan still has issues that stop approval",
        stillBlocked_other: "{{count}} applied plans still have issues that stop approval",
        allClear: "No approval-stopping issue in the applied plans' prepared scores",
        stale: "{{count}} not applied because the plan changed after its fix was prepared",
        failed: "{{count}} failed to save",
        skipped: "{{count}} skipped",
        cancelled: "{{count}} cancelled",
        nothingSafe_one: "1 store had no safe automatic fix and was not changed",
        nothingSafe_other: "{{count}} stores had no safe automatic fix and were not changed",
        notApproved:
          "Applying a fix does not approve a plan. Check the applied plans before approving them; each one can be undone from its plan.",
        reviewApplied: "Review applied plans ({{count}})",
        reviewBlocked: "Review plans still blocked ({{count}})",
        reviewUnchanged: "Review unchanged plans ({{count}})",
        reviewNothingSafe: "Review stores with no safe fix ({{count}})",
        dismiss: "Dismiss",
      },
      statuses: {
        queued: "queued",
        applying: "applying",
        kept: "applied",
        stale: "stale",
        skipped: "skipped",
        failed: "failed",
        cancelled: "cancelled",
        complete: "complete",
      },
    },
    title: "Planogram proofing",
    subtitle: "Review, fix, approve.",
    back: "Back to Space Planning",
    loading: "Loading the review queue…",
    scoring: "Scoring {{done}} of {{total}} planograms…",
    scopeMerchflow: "Merchflow {{id}}",
    scopeCount: "{{count}} planograms",
    empty: "Nothing to review",
    endOfQueue: "End of queue",
    keys: "Space play/pause · A approve · F flag · D describe · Ctrl+Z undo · ← → move · V compare view",
    bands: {
      fix: "Fix",
      review: "Review",
      audit: "Audit sample",
      recheck: "Re-check",
      recheckAgent: "Re-check · agent edit",
      unsampled: "unsampled",
    },
    gradingFailed: {
      approveBlocked:
        "This plan could not be graded: its CDT release or sent request did not load. Reload the page before approving it.",
    },
    scoreOutdated: {
      approveBlocked: "This plan is being graded on the current scorer. Try again in a moment.",
      counterHint:
        "Ordered on an older scorer's score until the backfill re-scores them. Each is graded again when it is on screen.",
    },
    counters: {
      approved: "approved",
      flagged: "flagged",
      described: "described",
      left: "left",
      unsaved: "unsaved",
      outdated: "older scores",
      elapsed: "Elapsed {{time}} · finish ≈ {{eta}}",
    },
    session: {
      saveFailed: "Some review decisions could not be saved.",
      queueFailed: "The review queue could not be loaded.",
    },
    problemGroups: {
      underDos: "Below minimum days of supply",
      coreRange: "Missing core products",
      fit: "Physical fit",
      checks: "Checks unavailable",
      fitBands: "Shelf placement rules",
      sequenceByBase: "Product order",
      tidyBlocking: "Separated product groups",
      fillGaps: "Shelf gaps",
      range: "Unplaced products",
      other: "Other checks",
      ready: "No flagged issues",
    },
    draftChecks: {
      checking: "Checking the proposed plan…",
      wait: "Wait for the checks before applying.",
      invalid: "The AI preview could not be checked.",
      incomplete: "Grading is incomplete: the preview score and checks are unavailable.",
      unverified: "The saved generation request or release could not be verified.",
      summary:
        "Score {{beforeScore}} → {{afterScore}} · {{before}} → {{after}} checks{{introduced}}",
      introduced: " · {{count}} new to review",
      physical: "This fix does not physically fit: {{issues}}. Ask AI to revise it.",
      waitForSaved: "Wait until the saved plan has loaded before keeping a fix.",
      saved: "Fix saved. Review the plan, then approve it.",
      savedNeedsChecks: "Fix saved. Use Retry checks before approving.",
      refreshed: "Checks refreshed.",
      refreshFailed: "Checks could not refresh. Try again.",
      unavailable: "Checks could not load. Retry checks before saving or approving.",
      changed: "Saved plan changed · refresh checks.",
      needsRefresh: "Fix saved · checks need refreshing.",
      retryBeforeApproval: "Retry checks before saving or approving.",
      saveAndApprove: "Save this fix and approve the saved plan.",
      alreadyApproved: "This saved revision is already approved.",
      previewOnly: "Saving and approval are not connected in this session yet.",
      approveAsIs: "Approve this saved plan as-is, including its remaining checks.",
      keepAndApprove: "Keep & Approve (A)",
      unsavedManual:
        "Unsaved manual edits. Keep the fix or return to the saved plan before moving on.",
      returnToSaved: "Return to saved plan",
      backToMerchflow: "Back to Merchflow",
      chooseMerchflow: "Choose a Merchflow to review its planograms.",
      store: "Store",
      planPosition: "Plan {{position}} of {{count}} in {{group}}",
      loadFailed: "Proofing could not be loaded",
      endOfGroup: " · End of group",
      queueFailed: "We couldn’t load these planograms. Your review decisions are retained.",
      retryQueue: "Retry loading proofing",
      fixPreviewDetails: "Fix preview details",
      previewUnsaved: "Preview only · nothing saved.",
      comparedOriginal: "Compared with the original.",
      changedBay: "Changed bay",
      dragHelp: "Drag products to edit. Select a product for more editing options.",
      wholePlan: "Whole plan",
      bay: "Bay {{number}}",
      issueSelected: "Issue {{number}} selected",
      dragToEdit: "Drag to edit",
      beforeReadOnly: "Before · read-only",
      editingControls: "Plan editing controls",
      reviewTools: "Review tools",
      selectedShelfRule: "Selected shelf placement rule",
      currentCheck: "Current check",
      previousPlan: "Previous plan",
      previousPlanKey: "Previous plan (←)",
      nextPlan: "Next plan",
      nextPlanKey: "Next plan (→)",
      secondsPerPlan: "Seconds per plan",
      shortcuts: "Shortcuts",
      before: "Before",
      after: "After",
      savedPlan: "Saved plan",
      savedLabel: "Saved",
      product: "Product",
      backToFixes: "Back to fixes",
      autofixChecks: "Autofix & checks",
      fixWithAi: "Fix with AI",
      shelfRule: "Shelf placement rule",
      savingFix: "Saving fix…",
      keepFix: "Keep this fix",
      saveThenApprove: "Save the fix, then approve.",
      attention: "What needs attention?",
      flagHelp: "Flagged for follow-up. Add a note or choose a reason (optional).",
      flagPlaceholder: "Describe what needs attention (optional)",
      saveNote: "Save note",
      unflag: "Unflag",
      approved: "Approved ✓",
      undoApproval: "Undo approval",
      undoLastApproval: "Undo last approval",
      saveApproveFix: "Save and approve this fix.",
      approvedEdit: "Approved plan · edit here to make another fix.",
      checksRemain: "Checks remain · approve as-is.",
      changeRemoved: "Removes {{count}} product displays from shelves",
      changeAdded: "Places {{count}} product displays on shelves",
      changeMoved: "Repositions {{count}} product displays",
      changeFacings: "Changes facings on {{count}} product displays",
      changeCapping: "Changes capping on {{count}} product displays",
      changeOther: "{{count}} product displays change",
      changeLocation: "{{changes}} in {{location}}.",
      changedBayLabel: "bay {{number}}",
      changedBaysLabel: "{{count}} bays",
      chooseChangedBay: "Choose changed bay",
      changedBayNumber: "Changed bay {{number}}",
      whatToCheck: "What to check",
      categoryOrder: "Products should follow the agreed grouping and sequence for this category.",
      showIssue: "Show this issue",
      run: "Run {{id}}",
      previewUnsavedBanner: "Preview · changes are unsaved.",
      newerRun: "View newer run {{id}}",
      unsavedEdit: "Unsaved edit",
      manualEdit: "Manual edit",
      hideProductControls: "Hide product controls",
      productControls: "Product controls",
      undoEdit: "Undo edit",
      redoEdit: "Redo edit",
      reviewChanges: "Review changes",
      doneEditing: "Done editing",
      discardEdits: "Discard edits",
      manualHelp:
        "Drag products to reorder them or move them to another shelf. Select a product for more editing options. Changes are unsaved until you keep or approve the fix.",
      allPlans: "All plans ({{count}})",
      dismiss: "Dismiss",
      approvalUndone: "Approval undone.",
      flagRemoved: "Flag removed.",
      refreshBeforeApproval: "Refresh the saved plans' checks before approving.",
      undoApprovalFailed: "Approval could not be undone. Retry or reload the plan.",
      unflagFailed: "The flag could not be removed. Try again.",
      savedReloadFailed: "The fix was saved, but checks could not reload. Retry before approving.",
      unchangedMove: "That move leaves the plan unchanged. Try a different product or shelf.",
      speed: "Speed · {{seconds}}s per plan",
      stockTarget: "Below stock target",
      stockCorePriorities: "Stock & core range priorities",
      invalidPreview:
        "This AI preview contains incomplete product or shelf data and cannot be displayed or saved. Your saved plan is unchanged. Ask AI to try again.",
      savedChecksUnavailable: "The saved plan's checks are unavailable. Retry checks.",
      revisionUnconfirmed:
        "The fix was saved but its revision could not be confirmed. Reload before approving.",
      approvalChanged: "Saved plan changed. Use Retry checks before approving.",
      approvalFailed: "Approval could not be saved. Try again.",
      approvalsChanged: "Saved plans changed. Use Retry checks before approving.",
      approvalsFailed: "Approvals could not be saved. Try again.",
      wrongPlanFix: "The automatic fix belongs to a different saved plan. Reload it.",
      wrongFixOption: "The automatic fix belongs to a different fix option. Try again.",
      queueChanged: "The saved plan changed; reload the Cinema queue before grading.",
      outsideMerchflow: "Review received a plan outside the selected Merchflow.",
    },
    controls: {
      paused: "PAUSED",
      play: "Play",
      pause: "Pause",
      previous: "Previous",
      next: "Next",
      approve: "Approve",
      flag: "Flag",
      describe: "Describe fix",
      fullscreen: "Full screen",
      exitFullscreen: "Exit full screen",
      dwell: "Dwell {{seconds}} s",
      autoApprove: "Approve on dwell",
      fixQueue: "Fix queue ({{count}})",
      openEditor: "Open in editor",
      retryChecks: "Retry checks",
      undoSavedFix: "Undo saved fix",
    },
    side: {
      score: "Aesthetics score",
      ungraded: "Not graded",
      bandFix: "below 70 · fix band",
      bandReview: "70–89 · review band",
      bandAudit: "90+ · audit sample",
      reasons: "Why the score says so",
      noReasons: "Nothing flagged by the score or the solver",
      loadingPlan: "Loading the plan…",
      worstBlock: "Worst block",
      emptiestShelf: "Emptiest shelf: run {{run}}, shelf {{shelf}}, {{gap}} in gap",
      whyBad: "Why is it bad?",
      describeTitle: "Describe the fix",
      describePlaceholder:
        "e.g. Move the 2-litre variants to the bottom shelf and re-block by brand left to right.",
      save: "Save",
      cancel: "Cancel",
      decided: "Decision",
      states: {
        SCORED: "Not seen yet",
        APPROVED: "Approved",
        FLAGGED: "Flagged",
        DESCRIBED: "Fix described",
        RECHECK: "Changed since last decision",
      },
    },
    view: {
      label: "Compare view",
      hint: "How the plan before and after is shown; V switches. Kept for every plan.",
      single: "Single",
      stacked: "Before / after",
      blink: "Blink",
    },
    autofix: {
      label: "Autofix",
      on: "On",
      off: "Off",
      onDetail:
        "Every safe fix goes on its plan as soon as it is ready. Approve keeps it, Undo puts the plan back.",
      offDetail:
        "Autofix is off for this merchflow: plans stay as generated and nothing new is prepared.",
      changing: "Changing…",
      loading: "Reading Autofix…",
      loadFailed: "Could not read whether Autofix is on.",
      retry: "Try again",
      approveHeld: "Autofix is switching. Approve again once it is done.",
      approveStale:
        "Autofix changed the plans and they have not been read again. Refresh status, then approve.",
      decisionsUnsaved: "Some review decisions could not be saved, so Autofix was not switched.",
      confirm_one: "Undo {{count}} fix not yet approved?",
      confirm_other: "Undo {{count}} fixes not yet approved?",
      confirmDetail:
        "Approved plans keep their fixes. Turning Autofix on again puts these fixes back.",
      confirmOff_one: "Undo {{count}} fix and turn off",
      confirmOff_other: "Undo {{count}} fixes and turn off",
      turnedOff: "Autofix is off: {{count}} fix(es) undone",
      turnedOn: "Autofix is on: {{count}} fix(es) back on their plans",
      skipped: "{{count}} plan(s) changed since and were left as they are",
    },
    readyGroups: {
      title: "Approve the {{count}} ready plans, grouped by the kind of fix on them",
      families: {
        "cdt-order": "CDT order fixed · {{count}}",
        blocks: "Blocks joined · {{count}}",
        "shelf-positions": "Shelves moved · {{count}}",
        other: "Other fixes · {{count}}",
        unchanged: "Not changed by Autofix · {{count}}",
      },
      kinds: {
        "cdt-order": "CDT order",
        blocks: "blocks joined",
        "shelf-positions": "shelves moved",
        other: "other fixes",
        unchanged: "not changed",
      },
      plan: "{{store}} {{before}} → {{after}}",
      review: "Review this group",
      approve_one: "Approve {{count}}",
      approve_other: "Approve {{count}}",
      sameKind_one:
        "{{count}} other plan got the same kind of fix ({{kind}}) and is ready. Approve it too?",
      sameKind_other:
        "{{count}} other plans got the same kind of fix ({{kind}}) and are ready. Approve them all?",
      dismiss: "Dismiss",
    },
    planAutofix: {
      leftAsIs: { title: "Autofix left this plan as is" },
      undone: {
        title: "You undid Autofix's fix",
        detail: "Score {{before}} → {{after}} with the fix. Redo puts it back.",
      },
      working: {
        title: "Autofix is working on this plan",
        detail: "Its fix goes on the plan as soon as it is ready.",
      },
      off: {
        title: "Autofix is off for this merchflow",
        detail: "This plan stays as generated. Turn Autofix on at the top to apply its fixes.",
      },
      redo: "Redo fix",
      redone: "Fix back on the plan",
      approveAsIs: "Approve as is (A)",
      notNeeded: "Nothing on this plan is something Autofix fixes.",
      notLooked: "Autofix has not looked at this plan yet.",
    },
    arrival: {
      changed: "Changed by Autofix · {{count}}",
      changedHint: "Approve keeps the fix, Undo puts the plan back.",
      changedStillStops:
        "{{count}} more changed still have a card that stops approval: they are under Needs you.",
      reviewChanged: "Review changes",
      leftAsIs: "Left as is · {{count}}",
      leftAsIsDetail: "Autofix did not change these, and no card stops approval.",
      needsYou: "Needs you · {{count}}",
      needsYouDetail: "A card still stops approval, with or without a fix.",
      working: "Autofix is still working on {{count}} plan(s); each joins a group when it is done.",
      play: "Play through",
      startNeedsYou: "Start with these",
      ready: "Ready to approve: {{count}} plans with no card that stops approval",
      readyNone: "Ready to approve: none yet",
      noneYet: "None yet",
      approveReady: "Approve all ready plans (keeps their fixes)",
      why: {
        fixedStillStops: "Changed, still needs you",
        noChange: "No change helps",
        breaksBlocks: "Would break other blocks",
        inPieces: "Would break blocks into pieces",
        greyOnly: "Only clears cards that don't stop approval",
        lowersScore: "Would lower the score",
        addsProblem: "Would add a problem",
        breaksRule: "Would break a hard rule",
        undone: "You undid the fix",
        autofixOff: "Autofix is off",
        notForAutofix: "Not something Autofix fixes",
        failed: "Couldn't be prepared",
        other: "Other",
      },
    },
    reel: {
      before: "Before · saved plan",
      after: "After · with the fix",
      beforeOriginal: "Before · original plan",
      afterApplied: "After · saved plan, fix applied",
    },
    issues: {
      title: "What is wrong",
      showOnPlan: "Show",
      hideOnPlan: "Hide",
      ignore: "Ignore",
      close: "Close",
      pinHint: "Pin {{number}} on the plan",
      builtWith: "For reference ({{count}})",
      ignored: "{{count}} ignored in this review",
      unignore: "Show again",
      ignoredToast: "Ignored on every plan in this review",
      approvable: "Does not stop approval",
      groups: {
        stops: "Needs review · {{count}}",
        look: "Worth a look · {{count}}",
        pieces: "Blocks in pieces · {{count}}",
        split: "Blocks split along a shelf · {{count}}",
        column: "Brands off their column · {{count}}",
        ragged: "Ragged edges · {{count}}",
        onlyBlocking: "(product grouping needs review)",
        blockingNote:
          "Several product groups need alignment. You can fix these or approve the current plan as-is.",
      },
      action: {
        applying: "Applying the fix…",
        redo: "Redo fix",
        redoWill: "Redo puts back the fix you undid: {{changes}}.",
        redoWillBare: "the plan as Autofix fixed it",
        editor: "Edit here",
        wouldSplit: "Autofix tried this plan: its fix would split {{blocks}} into pieces.",
        noChangeHelps: "Autofix tried: no change it can make helps this plan.",
        wouldBreakOthers: "Autofix tried this plan: its fix would break other blocks: {{blocks}}.",
        wouldAddProblem: "Autofix tried this plan: its fix would add a problem.",
        wouldLowerScore: "Autofix tried this plan: its fix would lower the score.",
        clearsOnlyMinor:
          "Autofix tried this plan: its fix would clear only cards that do not stop approval.",
        clearsNothing:
          "Autofix tried this plan: its fix would clear no card that stops approval, nor raise the score.",
        wouldBreakRules: "Autofix tried this plan: keeping its fix would break {{rules}}.",
        tried: "Autofix tried and stopped: {{reason}}",
        leftByFix: "Autofix fixed what it could on this plan and found no safe fix for this one.",
        preparing: "Autofix is still working on this plan; its fix goes on as soon as it is ready.",
        autofixOff: "Autofix is off for this merchflow.",
        noSafeFix: "Autofix found no safe fix for this.",
        planOptions: "Review checks. Fix or approve as-is.",
      },
      approvableHint:
        "You can approve this plan with this issue unresolved. It remains visible for review.",
      titles: {
        overlap: "Products overlap",
        pastEdge: "Past the shelf edge",
        emptyShelf: "Empty shelf",
        unplaced: "Products off the plan",
        eliminated: "Brand dropped",
        blockInPieces: "Block in pieces",
        blockStaircased: "Top-level column has uneven edges",
        blockOutOfShape: "Block out of shape",
        bandBroken: "Shelf placement rule not met",
        notFull: "Shelves not full",
        giantGap: "Giant gap on a shelf",
        blockPieces: "{{block}}: in {{pieces}} pieces",
        blockSplit: "{{block}}: split along a shelf",
        blockRagged: "{{block}}: ragged edges",
        blockColumn: "{{block}}: off its column",
        cdt: "Products out of planned order",
        gap: "Gap on a shelf",
        rulesKept: "Shelf rules broken",
        ergonomics: "Heavy or big packs up high",
        sizeVertical: "Small packs under big",
        solver: "Relaxed: {{rule}}",
        human: "Flagged by a reviewer",
      },
      rules: {
        shelf_segment_space: "Segment short of its space",
        facings_too_many: "Facings over the cap",
        facings_not_enough: "Facings under the minimum",
        fixed_notch_num: "Shelf off its fixed height",
        cdt1_order: "Top-level groups out of order",
        cdt2_order: "Second-level groups out of order",
        cdt3_order: "Third-level groups out of order",
        cdt1_contiguous: "Top-level group split",
        cdt2_contiguous: "Second-level group split",
        cdt3_contiguous: "Third-level group split",
        item_span: "Facings straddle a boundary",
        rows_not_enough: "Stacked fewer rows than required",
        min_distribution: "Under minimum distribution",
        units_not_enough_dos: "Under its days of supply",
        units_not_enough_mos: "Under its minimum on shelf",
        units_too_many: "Units over the cap",
        min_width: "Under its minimum width",
        item_id_order: "Not in the listed sequence",
      },
      where: {
        example: "{{example}}",
        exampleMore: "{{example}} +{{more}}",
        unplaced: "{{count}} off: {{names}}",
        names: "{{names}}",
        block: "Shelves {{shelves}}",
        blockRagged: "Shelves {{shelves}} · {{packs}} packs out",
        gap: "Bay {{bay}}, shelf {{shelf}} · {{gap}} {{unit}}",
        giantGap: "Run {{run}}, bay {{bay}}, shelf {{shelf}} · {{inches}} {{unit}} empty",
        giantGapBays:
          "Run {{run}}, bays {{first}}-{{last}}, shelf {{shelf}} · {{inches}} {{unit}} empty",
        ordering: "{{block}} · {{count}} out of order",
        blockOrder: "Product groups {{percent}}% in planned product order",
        percent: "{{percent}}%",
        sizeVertical:
          "Bay {{bay}} {{segment}}: {{lower}} {{unit}} on shelf {{lowerShelf}} under {{upper}} {{unit}}",
        solver: "{{names}} · bay {{bay}}, shelf {{shelf}}",
        solverCount: "{{count}} at shelf level",
        split: "{{group}} · {{runs}} runs on bay {{bay}}, shelf {{shelf}}",
      },
      detail: {
        split: "Bay {{bay}}, shelf {{shelf}}: {{runs}} runs",
        column: "Column on shelves {{shelves}}, leaves shelves {{missing}} to other brands",
        rule: "{{name}} · shelf {{shelf}} ({{band}})",
        sizeVertical:
          "Bay {{bay}} {{segment}}: {{lower}} {{unit}} under {{upper}} {{unit}} (shelf {{lowerShelf}})",
        more: "+{{count}} more",
      },
      shelfRule: {
        title: {
          prohibited: "Not allowed on this shelf: {{name}}",
          required: "Required product group missing: {{name}}",
          reserved: "Wrong product in a reserved shelf area: {{name}}",
        },
        location: "Bay {{bay}}, shelf {{shelf}} from top",
        locations: "{{locations}}",
        unresolved: "The exact governed shelf is unavailable in the saved rule context.",
        expected: {
          prohibited: "Expected: products in “{{band}}” must not be on this shelf.",
          required: "Expected: this shelf area must contain products in “{{band}}”.",
          reserved: "Expected: this shelf area is reserved for “{{band}}” products only.",
        },
        actual: {
          placed: "Actual: {{name}} is placed here. Facings: {{facings}}.",
          missing: "Actual: no products in “{{band}}” are placed in the governed area.",
          unresolved:
            "The score reported {{name}} ({{facings}} facings) on row {{shelf}}; its exact physical shelf is unavailable.",
        },
        shownSide: "Showing: {{side}}",
        resolvedTitle: "Shelf placement rule satisfied",
        resolved:
          "This shelf placement rule is satisfied in the plan shown. No offending display is highlighted.",
        requiredPosition: "Required position for this group: {{locations}}.",
      },
    },
    reasons: {
      "human:merchandising": "Product placement",
      "human:brand-adjacency": "Brands next to each other",
      "human:product-image": "Wrong product image",
      "human:shopper-flow": "Shopper navigation",
      "human:other": "Other",
      "audit:unsampled": "Audit band, approved in bulk",
      allRules: "all relaxed rules",
      prefixes: {
        gate: "Gate",
        aesthetics: "Score",
        solver: "Solver",
        human: "Human",
        audit: "Audit",
      },
    },
    auto: {
      didTitle: "What the agent did to this plan",
      run: "Autofix this plan",
      changed: "A fix was written over this plan. The plan as it was is still held.",
      revertOriginal: "Back to the original plan",
      all: "Autofix all ({{count}})",
      allTitle: "Autofix",
      allProgress:
        "{{done}} of {{total}} plans · {{changed}} changed · {{unchanged}} left as they were",
      allDone:
        "Done: {{done}} of {{total}} plans · {{changed}} changed and back in the player as re-checks · {{unchanged}} left as they were",
      allFailed: "{{count}} could not be read or written",
      stop: "Stop",
      stopping: "Stopping after this batch…",
    },
    proposals: {
      changes: {
        productsMoved_one: "moves {{count}} product",
        productsMoved_other: "moves {{count}} products",
        facingsChanged_one: "{{count}} facing",
        facingsChanged_other: "{{count}} facings",
        shelvesMoved_one: "moves {{count}} shelf",
        shelvesMoved_other: "moves {{count}} shelves",
      },
      showOriginal: "Show the original",
      showApplied: "Show the applied plan",
      undoKept: "Undo the kept proposal",
      retry: "Retry",
      approveBlocked: "Autofix is still working on this plan: approve once its fix is on",
      kept: "Autofix proposal kept",
      nothingSafe: "Autofix found nothing safe to do: {{reason}}",
      prepared: "{{count}} plan(s) queued for Autofix proposals",
    },
    fixSummary: {
      title: "Autofix changed this plan",
      score: "Score",
      stops: "Cards that stop approval",
      hint: "The fix is on the plan, not approved yet.",
      approve: "Approve with fix (A)",
      undo: "Undo fix (U)",
      flag: "Flag (F)",
      beforeAfter: "Before / after (V)",
      effects: {
        "reorder-products_one": "reordered {{count}} product on its shelf",
        "reorder-products_other": "reordered {{count}} products on their shelves",
        "move-between-shelves_one": "moved {{count}} product to another shelf",
        "move-between-shelves_other": "moved {{count}} products to another shelf",
        "increase-facings_one": "added {{count}} facing",
        "increase-facings_other": "added {{count}} facings",
        "decrease-facings_one": "removed {{count}} facing",
        "decrease-facings_other": "removed {{count}} facings",
        "move-shelves_one": "moved {{count}} shelf",
        "move-shelves_other": "moved {{count}} shelves",
        "reorient-products_one": "turned {{count}} product",
        "reorient-products_other": "turned {{count}} products",
        "resize-products_one": "changed the squeeze of {{count}} product",
        "resize-products_other": "changed the squeeze of {{count}} products",
        "add-products_one": "added {{count}} product",
        "add-products_other": "added {{count}} products",
        "remove-products_one": "removed {{count}} product",
        "remove-products_other": "removed {{count}} products",
        "join-blocks_one": "joined {{count}} block",
        "join-blocks_other": "joined {{count}} blocks",
      },
    },
    queue: {
      title: "Fix queue",
      subtitle:
        "Flags grouped by the fix that clears them and their base planogram. Describe a fix once per cluster.",
      empty: "Nothing flagged yet. Flag planograms with F and they group here by reason.",
      cluster: "{{count}} planogram(s)",
      describeCluster: "Describe the fix once for this cluster",
      saveCluster: "Save for {{count}}",
      approveUnsampled: "Approve unsampled audit band ({{count}})",
      approvedUnsampled: "Approved {{count}} planograms from the audit band",
      skippedUnsampled:
        "{{count}} plan(s) have an Autofix proposal waiting: keep or set it aside first",
      merchflow: "Merchflow {{id}}",
      basePog: "base POG {{id}}",
      families: {
        fitBands: "Shelf bands",
        sequenceByBase: "Sequence by the base",
        tidyBlocking: "Tidy blocking",
        fillGaps: "Fill gaps",
      },
    },
    agent: {
      preview: "Preview fix on {{store}}",
      previewing: "Asking the agent…",
      exemplar: "Try it on",
      allowCode: "Allow generated code",
      prompt: "What the agent reads",
      support: {
        catalog: "Agent can do this",
        codegen: "Needs generated code",
        none: "Agent can't fix this",
      },
      before: "Before",
      after: "After",
      changes: "Changes",
      noChange:
        "The agent did not change anything. Rephrase the note with product codes and a shelf.",
      warnings: "Warnings",
      code: "Generated code",
      message: "Agent said",
      approve: "Apply to {{count}} planogram(s)",
      approveOne: "This one only",
      reject: "Not what I meant",
      rejectFeedback: "What was wrong? (optional, kept with the run)",
      manualFallback: "Two previews rejected. Fix this cluster by hand.",
      applying: "Applying {{done}} of {{total}}…",
      stop: "Stop after this chunk",
      summary:
        "{{written}} written · {{skipped}} unchanged · {{failed}} failed · {{stale}} changed meanwhile",
      retryFailed: "Retry failed ({{count}})",
      recheckHint:
        "Written plans are back in the player as re-checks. Nothing is approved for you.",
      status: {
        written: "written",
        skipped: "unchanged",
        failed: "failed",
        stale: "changed meanwhile",
      },
      diff: {
        moved: "moved",
        facings: "facings",
        orientation: "orientation",
        squeeze: "squeeze",
        shelf: "Bay {{bay}} · shelf {{shelf}}",
      },
      errors: {
        notConfigured: "The POG agent is not configured for this environment.",
        handoverExpired: "The agent's plan expired. Preview the fix again.",
        exemplarChanged: "That planogram changed since you looked at it. Preview again.",
        sessionExpired: "Your session expired. Sign in again and retry.",
        failed: "The agent could not do this: {{detail}}",
      },
    },
    toasts: {
      approved: "Approved",
      approvedMany: "Approved {{count}} plans",
      flagged: "Flagged",
      described: "Fix description saved",
      fullscreenUnavailable: "Full screen is not available here",
    },
  },
  storeViewer: {
    title: "Store POG viewer",
    readOnly: "Read only",
    summary: "Store {{store}} · {{category}} · {{merchflow}}",
    summaryApproved: "Store {{store}} · {{category}} · {{merchflow}} · approved",
    previous: "Previous",
    next: "Next",
    position: "{{position}} of {{total}}",
    backToSpacePlanning: "Back to Space Planning",
    highlight: "Highlight",
    highlightModes: {
      none: "None",
      segment: "Segment",
      brand: "Brand",
      variant: "Variant",
    },
    showImages: "Show images",
    showAvailableSpace: "Show available space",
    loading: "Store planogram is loading...",
    loadFailed: "Store planogram failed to load",
    notFound: "Store planogram not found",
    retry: "Retry",
    metrics: {
      coreRange: "Core range",
      shelfAlignment: "Shelf alignment",
      dosMos: "DOS / MOS",
    },
    salesSnapshot: "Sales (POG snapshot)",
    salesLatest: "Sales (latest load)",
  },
  proofing: {
    canvas: {
      view: "View",
      focusBay: "Focus bay",
      wholePlan: "Whole plan",
      bay: "Bay {{number}}",
      fitPlan: "Fit plan",
      comparisonViews: "Comparison views",
      comparePlans: "Compare plans",
      single: "Single view",
      singleHelp: "Choose Before or After to show one plan at a time.",
      stacked: "Show both",
      stackedHelp: "See before and after together, one above the other.",
      blink: "Blink",
      blinkHelp: "Flash between saved and proposed plans.",
      explanation: "{{label}} explanation",
      notReadyHint: "Select a mode now; comparison appears when a fix is ready.",
      savedPlan: "Saved plan",
      beforePhase: "BEFORE",
      afterPhase: "AFTER",
      before: "Before",
      after: "After",
      showSide: "Show comparison side",
      savedPlanLabel: "saved plan",
      originalPlanLabel: "original plan",
      proposedFixLabel: "proposed fix",
      savedFixLabel: "saved fix",
    },
    strip: {
      label: "Review by problem",
      groupTitle:
        "Review these {{count}} planograms together. Next and Play stay within {{group}}.",
      groupOne: "{{label}} · {{count}} plan",
      groupMany: "{{label}} · {{count}} plans",
    },
    autofix: {
      heading: "Autofix",
      on: "Autofix On",
      off: "Autofix Off",
      options: "Autofix options",
      layout: "Layout",
      capping: "Add capping",
      cappingHelp: "Capping adds products above existing stacks where space and shelf rules allow.",
      isOff: "Autofix is off.",
      checking: "Checking for fixes…",
      failed: "Autofix could not finish.",
      retry: "Retry Autofix",
      levellingReady: "Stack levelling ready",
      cappingReady: "Capping ready",
      layoutReady: "Layout preview ready",
      noFix: "No checked fix found",
      partialSearch: "The search stopped before testing every option. A fix may still be possible.",
      keep: "Keep fix",
      preview: "Preview fix",
      returnToSaved: "Return to saved plan",
      undo: "Undo Autofix",
      fixWithAi: "Fix with AI",
      nothingToKeep: "There is no automatic change to keep.",
      planChanged: "The plan changed since this fix. Reload before undoing.",
      limitWidth: "A shelf already exceeds its usable width.",
      limitDepth: "Some changes exceed usable depth or stock limits.",
      limitGrouping: "Some changes would break product grouping or other layout checks.",
    },
    scores: {
      metrics: {
        dosMos: "Stock cover score",
        coreRange: "Core range",
        returnOnSpace: "Return on Space",
        shelfAlignment: "Shelf alignment",
        aesthetics: "Aesthetics",
      },
      scorecard: "Plan scorecard",
      daysOfSupply: "Days of supply",
      daysOfSupplyHelp:
        "Days-of-supply and minimum-on-shelf compliance by display. The priority count totals stock across all displays of each product to identify shortages; the percentage is not a number of days.",
      dosMosHelp:
        "How closely stock matches the days/months targets. A compliance score, not a number of days.",
      showBelowMinimum: "Show products below minimum days of supply",
      showMissingCore: "Show missing core-range products",
      notConfigured: "Not configured",
      notAvailable: "Not available",
      changeNotAvailable: "Change not available",
      noChange: "No change",
      points: "{{change}} points",
      percentagePoints: "{{change}} percentage points",
      belowMinimum: "{{count}} below minimum",
      coreMissing: "{{count}} core products missing",
      before: "Before",
      after: "After",
      saved: "Saved",
      fiveScores: "Five plan scores",
      beforeAfterScores: "Before and after scores",
      savedScores: "Saved plan scores",
      score: "Score",
      change: "Change",
      changeInPoints: "Change in points",
      notAvailableForPlan: "— Not available for this plan",
    },
  },
} as const;

export type SpacePlanningTranslations = typeof spacePlanningEn;
